import * as React from 'react';
import { connect } from 'react-redux';
import { graphql, QueryRenderer, Environment } from 'react-relay';
import { Flex, Box } from 'grid-styled';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { LDFlagSet } from 'launchdarkly-js-sdk-common';
import styled from '@emotion/styled';
import { styles as s } from '@onehope/design-system-ohw';
import { Case4CauseToastQuery } from './__generated__/Case4CauseToastQuery.graphql';
import { getC4CToastText } from './getC4CToastText';
import { TYPES } from './types';
import { Case4CauseBottles } from './Case4CauseBottles';

/* Style */
import '../../../styles/common.scss';
import './Case4CauseToast.scss';

const { cssConstants } = s;

const BronzeText = styled.span`
  font-size: 12px;
  color: ${cssConstants.bronzeNeutral0};
`;
const BlackText = styled.span`
  font-size: 12px;
  color: ${cssConstants.black2};
`;

interface ToastProps {
  cartQty: number;
  pcPotentialSavings: number;
  subTotalPriceOfItems: number;
  isUserPC: boolean;
  handleCartToggle: () => void;
  componentType: string;
  hideText?: boolean;
  enrollingAsPC: boolean;
  flags?: LDFlagSet;
}

function isUserPCCheck(user: any) {
  return (
    user.permissions.oneCommerce &&
    user.permissions.oneCommerce.preferredCustomer &&
    user.permissions.oneCommerce.preferredCustomer.access
  );
}

function Case4CauseToastBase(props: ToastProps) {
  const {
    cartQty,
    componentType,
    hideText = false,
    pcPotentialSavings,
    isUserPC,
    enrollingAsPC,
    flags,
  } = props;
  const c4cCartTotal = cartQty >= 0 ? cartQty : 0;
  const qtyInCart = c4cCartTotal % 12;
  const isDiscount20 = c4cCartTotal >= 12 && qtyInCart === 0;
  const isDiscount10 =
    c4cCartTotal < 12 && c4cCartTotal >= 6 && qtyInCart === 0;
  const isDiscount5 =
    (c4cCartTotal < 6 && c4cCartTotal >= 4) ||
    (c4cCartTotal < 18 && c4cCartTotal >= 16);
  const text = getC4CToastText({
    c4cCartTotal,
    qtyInCart,
    isDiscount20,
    pcPotentialSavings,
    isUserPC,
    componentType,
    enrollingAsPC,
    flags,
  });

  const isUpsellHeader = componentType === TYPES.UPSELL_HEADER;
  const toastContainerStyle = hideText ? { marginTop: '16px' } : undefined;
  return (
    <Flex justify="center" className={componentType}>
      <Box className="ToastSavingsContainer" style={toastContainerStyle}>
        {!hideText && (
          <Flex
            justify="center"
            data-cypress="toast-text"
            className={
              isUpsellHeader ? 'ToastText ToastTextPCUpsell' : 'ToastText'
            }
          >
            <Box>
              <BronzeText>{text.bronzeText}</BronzeText>
              <BlackText>{text.blackText}</BlackText>
              {/* {text.map((line, index) => (
                <span key={`${hideText}${line}${index}`}>{line}</span>
              ))} */}
            </Box>
          </Flex>
        )}
        <Flex justify="center" className="ToastWineBottles">
          <Box>
            <Case4CauseBottles
              cartQty={cartQty}
              isDiscount20={isDiscount20}
              flags={flags}
              isDiscount10={isDiscount10}
              isDiscount5={isDiscount5}
            />
          </Box>
        </Flex>
      </Box>
      <Box />
    </Flex>
  );
}

export const Case4CauseToast = withLDConsumer()(Case4CauseToastBase);

const HyperOptimizedComponent = connect((state: any) => ({
  // enrollingAsPC: state.cart.enrollingAsPc,
  // isUserPC: state.cart.isUserPC,
}))(Case4CauseToast);

type ToastRendererProps = {
  environment: Environment;
  accountId: string;
};

const ToastRenderer = ({
  environment,
  accountId,
  ...props
}: ToastRendererProps) => {
  return (
    <QueryRenderer<Case4CauseToastQuery>
      variables={{ accountId }}
      environment={environment}
      query={graphql`
        query Case4CauseToastQuery {
          viewer {
            id
            id
            user {
              id
              id
              permissions {
                id
                oneCommerce {
                  id
                  ceo {
                    access
                  }
                  preferredCustomer {
                    access
                  }
                }
              }
              cart {
                id
                id
                viaQualifiedCartTotal
                subTotalPriceOfItems
                promoCodeSavings
                pcPotentialSavings
                enrollingAsPc
              }
              conciergeUser {
                id
                permissions {
                  id
                  oneCommerce {
                    id
                    ceo {
                      access
                    }
                    preferredCustomer {
                      access
                    }
                  }
                }
                cart {
                  id
                  id
                  viaQualifiedCartTotal
                  subTotalPriceOfItems
                  promoCodeSavings
                  pcPotentialSavings
                  enrollingAsPc
                }
              }
            }
          }
        }
      `}
      render={({ error, props: propsFromRender }) => {
        if (error) {
          return <div />;
        }
        if (
          propsFromRender &&
          propsFromRender.viewer &&
          propsFromRender.viewer.user &&
          propsFromRender.viewer.user.cart
        ) {
          const user =
            propsFromRender.viewer.user.conciergeUser ||
            propsFromRender.viewer.user;
          const {
            viaQualifiedCartTotal,
            subTotalPriceOfItems,
            promoCodeSavings,
            pcPotentialSavings,
            enrollingAsPc,
          } = user.cart;
          const isUserPC = isUserPCCheck(user);
          return (
            <HyperOptimizedComponent
              {...{
                ...props,
                cartQty: viaQualifiedCartTotal,
                subTotalPriceOfItems,
                promoCodeSavings,
                pcPotentialSavings,
                isUserPC,
                enrollingAsPC: enrollingAsPc,
              }}
            />
          );
        }
        return <div />;
      }}
    />
  );
};

export default withLDConsumer()(ToastRenderer);
